import { createStore } from "zustand/vanilla";
import { between, deepEqual } from "@pack-it-up/shared";
import { ok } from "@pack-it-up/shared";
import { advanceCategoryStaging as advanceCategoryStagingPlan, advanceStagingState, applyToggle, clearCompletions, completionDelta, deriveModified, emptyCompletion, resetItemToImport, } from "@pack-it-up/core";
import { emptyReconcile, onSnapshot, list, get as rget, } from "./reconcile.js";
import { memoizedOverSlice } from "./memo.js";
import { createOptimisticRunner } from "./optimistic.js";
import { errorToMessage } from "../errorToMessage.js";
export const createItemsStore = (deps) => {
    const rows = memoizedOverSlice((s) => list(s));
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const store = createStore((set, getState) => {
        const runner = createOptimisticRunner({
            entity: "item",
            deltaPrefix: "item-delta",
            getSlice: () => getState().slice,
            set,
            ...(deps.writeTimeoutMs !== undefined ? { timeoutMs: deps.writeTimeoutMs } : {}),
        });
        const optimistic = runner.mutate;
        return {
            slice: emptyReconcile(),
            error: null,
            undoSnapshot: null,
            items: () => rows(getState().slice),
            async create(input) {
                const r = await deps.items.createItem(input); // new id from repo; stream delivers it
                if (!r.ok)
                    set({ error: errorToMessage(r.error) });
                return r;
            },
            update: (patch) => {
                // Set by the mutator below when the edit flips the cached provenance flag, so
                // the same write that persists the content also persists the flag (Doc 02 §7).
                let modified;
                return optimistic(patch.itemId, (cur) => {
                    const next = { ...cur };
                    if (patch.name !== undefined)
                        next.name = patch.name;
                    if (patch.quantity !== undefined)
                        next.quantity = patch.quantity;
                    // `null` clears the field (Doc 12 §1.3); a string sets it.
                    if (patch.description !== undefined) {
                        if (patch.description === null)
                            delete next.description;
                        else
                            next.description = patch.description;
                    }
                    if (patch.notes !== undefined) {
                        if (patch.notes === null)
                            delete next.notes;
                        else
                            next.notes = patch.notes;
                    }
                    // Tags are stored absent rather than empty. They are snapshot content
                    // (T29), so a tag-only edit does flip modifiedSinceImport below.
                    if (patch.tags !== undefined) {
                        if (patch.tags === null || patch.tags.length === 0)
                            delete next.tags;
                        else
                            next.tags = patch.tags;
                    }
                    // Re-derive the cached flag against the import snapshot (Doc 02 §7, Doc 06
                    // §10): editing raises it, editing back to the imported content clears it.
                    if (next.provenance) {
                        const m = deriveModified(next);
                        if (m !== next.provenance.modifiedSinceImport) {
                            modified = m;
                            next.provenance = { ...next.provenance, modifiedSinceImport: m };
                        }
                    }
                    return next;
                }, () => deps.items.updateItem(modified === undefined ? patch : { ...patch, modifiedSinceImport: modified }));
            },
            setAssignment: (itemId, assignment) => 
            // Assignment is a view over the completion record — changing it never touches checks.
            optimistic(itemId, (cur) => ({ ...cur, assignment }), () => deps.items.setAssignment(itemId, assignment)),
            async toggle(itemId, target, targetIsUser) {
                const cur = rget(getState().slice, itemId);
                if (!cur)
                    return null;
                const res = applyToggle(cur, deps.actor, target, targetIsUser, {
                    allowProxyCompletion: deps.allowProxyCompletion?.() ?? false,
                });
                if (!res.ok) {
                    set({ error: "You can't check another person's box." });
                    return null;
                }
                // Persist each changed participant path: one for an ordinary toggle, several when a
                // shared uncheck clears multiple preserved entries at once.
                const writes = completionDelta(cur.completions, res.item.completions);
                return optimistic(itemId, () => res.item, async () => {
                    const results = await Promise.all(writes.map(([pid, done]) => deps.items.setCompletion(itemId, pid, done)));
                    return results.find((r) => !r.ok) ?? ok(undefined);
                });
            },
            async advanceStaging(itemId, target, targetIsUser) {
                const cur = rget(getState().slice, itemId);
                if (!cur)
                    return null;
                const res = advanceStagingState(cur, deps.actor, target, targetIsUser, deps.stagingEnabled?.() ?? false, {
                    allowProxyCompletion: deps.allowProxyCompletion?.() ?? false,
                });
                if (!res.ok) {
                    set({ error: "You can't check another person's box." });
                    return null;
                }
                const completionWrites = completionDelta(cur.completions, res.item.completions);
                const stagingWrites = completionDelta(cur.staging ?? emptyCompletion(), res.item.staging ?? emptyCompletion());
                return optimistic(itemId, () => res.item, async () => {
                    const results = await Promise.all([
                        ...completionWrites.map(([pid, done]) => deps.items.setCompletion(itemId, pid, done)),
                        ...stagingWrites.map(([pid, done]) => deps.items.setStaging(itemId, pid, done)),
                    ]);
                    return results.find((r) => !r.ok) ?? ok(undefined);
                });
            },
            async toggleStaged(itemId, target, targetIsUser) {
                const cur = rget(getState().slice, itemId);
                if (!cur)
                    return null;
                const res = applyToggle(cur, deps.actor, target, targetIsUser, {
                    allowProxyCompletion: deps.allowProxyCompletion?.() ?? false,
                    field: "staging",
                });
                if (!res.ok) {
                    set({ error: "You can't check another person's box." });
                    return null;
                }
                const writes = completionDelta(cur.staging ?? emptyCompletion(), res.item.staging ?? emptyCompletion());
                return optimistic(itemId, () => res.item, async () => {
                    const results = await Promise.all(writes.map(([pid, done]) => deps.items.setStaging(itemId, pid, done)));
                    return results.find((r) => !r.ok) ?? ok(undefined);
                });
            },
            async advanceCategoryStaging(categoryId, trip) {
                const before = list(getState().slice);
                const after = advanceCategoryStagingPlan(before, categoryId, trip, deps.actor, deps.stagingEnabled?.() ?? false, {
                    allowProxyCompletion: deps.allowProxyCompletion?.() ?? false,
                });
                let next = getState().slice;
                const writes = [];
                for (let i = 0; i < before.length; i++) {
                    const b = before[i];
                    const a = after[i];
                    if (a === b)
                        continue;
                    next = onSnapshotReplace(next, a);
                    for (const [pid, done] of completionDelta(b.completions, a.completions)) {
                        writes.push(deps.items.setCompletion(b.id, pid, done));
                    }
                    for (const [pid, done] of completionDelta(b.staging ?? emptyCompletion(), a.staging ?? emptyCompletion())) {
                        writes.push(deps.items.setStaging(b.id, pid, done));
                    }
                }
                set({ slice: next });
                await Promise.all(writes);
            },
            move: (itemId, toCategoryId, neighbors) => {
                const order = between(neighbors.before ?? null, neighbors.after ?? null);
                return optimistic(itemId, (cur) => ({ ...cur, categoryId: toCategoryId, order }), () => deps.items.moveItem(itemId, toCategoryId, order));
            },
            async clearAll(scope) {
                const before = list(getState().slice);
                const cleared = clearCompletions(before, scope);
                const clearedById = new Map(cleared.map((c) => [c.id, c]));
                // Build the per-row inverse and optimistically clear only the rows whose
                // completions actually changed in value. (Value equality, not ===: the "all"
                // scope news up an empty map even for already-empty rows, and we must not
                // record those — otherwise undo would clobber a concurrent change to them.)
                const undoRows = [];
                let next = getState().slice;
                for (const item of before) {
                    const after = clearedById.get(item.id);
                    if (deepEqual(after.completions, item.completions))
                        continue;
                    undoRows.push({ itemId: item.id, completions: item.completions, restore: doneParticipants(item) });
                    next = onSnapshotReplace(next, after);
                }
                set({ slice: next, undoSnapshot: undoRows, error: null });
                // Persist by clearing each affected participant's completion path.
                await Promise.all(undoRows.flatMap((u) => u.restore.map((p) => deps.items.setCompletion(u.itemId, p, false))));
            },
            async undo() {
                const undoRows = getState().undoSnapshot;
                if (!undoRows)
                    return;
                set({ undoSnapshot: null, error: null });
                // Restore only the cleared rows' completions, merged onto the *current* slice,
                // so any concurrent changes to other rows since the clear survive.
                let next = getState().slice;
                for (const u of undoRows) {
                    const cur = rget(next, u.itemId);
                    if (cur)
                        next = onSnapshotReplace(next, { ...cur, completions: u.completions });
                }
                set({ slice: next });
                // Re-issue the completions so the undo persists (per-participant LWW writes).
                await Promise.all(undoRows.flatMap((u) => u.restore.map((p) => deps.items.setCompletion(u.itemId, p, true))));
            },
            async resetToImport(itemId) {
                const cur = rget(getState().slice, itemId);
                if (!cur || !cur.provenance?.createdByImport)
                    return;
                const reset = resetItemToImport(cur);
                // Optimistically restore the content locally, then persist the writable fields
                // (name/qty/description/notes via updateItem; assignment via setAssignment).
                set({ slice: onSnapshotReplace(getState().slice, reset), error: null });
                await Promise.all([
                    deps.items.updateItem({
                        itemId,
                        name: reset.name,
                        quantity: reset.quantity,
                        description: reset.description ?? null,
                        notes: reset.notes ?? null,
                        // Tags are snapshot content (T29): a reset restores the template's, or
                        // clears the row's when the snapshot carried none.
                        tags: reset.tags ?? null,
                    }),
                    deps.items.setAssignment(itemId, reset.assignment),
                ]);
            },
            remove: (itemId) => runner.remove(itemId, () => deps.items.deleteItem(itemId)),
            dispose() {
                for (const unsubscribe of subs.splice(0))
                    unsubscribe();
            },
        };
    });
    subs.push(deps.items.observeItems(deps.listId).subscribe((items) => {
        store.setState({ slice: onSnapshot(store.getState().slice, items, (i) => i.id) });
    }));
    return store;
};
// Replace a single row in a reconcile slice (used for the local optimistic clear).
const onSnapshotReplace = (s, item) => {
    const byId = new Map(s.byId);
    byId.set(item.id, item);
    return { byId, pending: s.pending };
};
// The participant paths currently marked done on an item — i.e. the writes a clear
// must undo: every participant whose entry is `done`.
const doneParticipants = (item) => Object.entries(item.completions.byParticipant)
    .filter(([, entry]) => entry.done)
    .map(([pid]) => pid);
//# sourceMappingURL=itemsStore.js.map